import React, { memo } from 'react'
import times from 'lodash/times'
import Skeleton from 'transfix-ui/core/Skeleton'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { Maybe } from 'types/graphqlTypes'
import { formatNumber } from 'utils/formatCurrency'
import { IShipmentCardInfoFragment } from '../../graphql/ShipmentCardInfoFragment'

const useShipmentDetailsCardMetricsStyles = makeStyles(theme => ({
  metrics: {
    margin: `${theme.spacing(1)} 0 0`,
    '& .divider': {
      margin: `0 ${theme.spacing(1)}`,
      display: 'inline',
      color: brandColors.coolGray5,
    },
  },
  metricsSkeleton: {
    display: 'flex',
    alignItems: 'center',
  },
}))

const Divider = memo(() => {
  return <div className='divider'>&middot;</div>
})

export interface IShipmentDetailsCardMetricsProps
  extends Pick<
    IShipmentCardInfoFragment,
    'calculatedMiles' | 'team' | 'totalPalletCount' | 'totalWeight'
  > {
  loading: boolean
  equipmentName: Maybe<string>
  equipmentParentName: Maybe<string>
}

const ShipmentDetailsCardMetrics = memo<IShipmentDetailsCardMetricsProps>(
  ({
    calculatedMiles,
    equipmentName,
    equipmentParentName,
    loading,
    team,
    totalPalletCount,
    totalWeight,
  }) => {
    const classes = useShipmentDetailsCardMetricsStyles()

    const weightLabel = `${formatNumber(totalWeight)} lbs`
    const palletCountLabel = `${totalPalletCount} pallet${totalPalletCount !== 1 ? 's' : ''}`
    const drivingTypeLabel = team ? 'Team Driver' : 'Single Driver'
    const milesLabel = `${formatNumber(calculatedMiles)} mile${calculatedMiles !== 1 ? 's' : ''}`

    return (
      <div className={classes.metrics}>
        {!loading && (
          <Typography variant='caption' color='textSecondary' component='div'>
            <span>{weightLabel}</span>
            <Divider />
            <span>{palletCountLabel}</span>
            <Divider />
            <span>{equipmentParentName}</span>
            <Divider />
            <span>{equipmentName}</span>
            <Divider />
            <span>{drivingTypeLabel}</span>
            <Divider />
            <span>{milesLabel}</span>
          </Typography>
        )}
        {loading && (
          <div className={classes.metricsSkeleton}>
            {times(6).map(i => (
              <>
                <Skeleton key={i} variant='text' width={50} height={16} />
                {i !== 5 && <Divider />}
              </>
            ))}
          </div>
        )}
      </div>
    )
  }
)

export default ShipmentDetailsCardMetrics
